import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ITeam } from '@core/interfaces/team.interface';
import { Observable, Subject } from 'rxjs';
import { AddTeamDialogComponent } from '../add-team-dialog/add-team-dialog.component';
import { SelectTeamDialogComponent } from './select-team-dialog.component';
import {
  ISelectTeamDialogData,
  ISelectTeamDialogResult,
  TSelectTeamDialogCloseResult,
} from './select-team-dialog.interface';

/**
 * Orchestrates the Select Team dialog together with the "Add team" round-trip it can launch -
 * same shape as SelectRoleDialogService for Select Role/Add Custom Role, see that service's doc
 * comment for the full reasoning. `open()` collapses the possibly-multi-dialog flow into one
 * Observable that always emits the up-to-date teams list (so the caller can keep its own teams
 * state in sync even if a team was created along the way) alongside the final chosen team id.
 *
 * `onSave` is threaded through every Select Team instance opened along the way (including after
 * an Add Team detour) - the dialog itself calls it and owns the request lifecycle, see
 * ISelectTeamDialogData's doc comment. `savedTeamId` is threaded through unchanged too, even
 * though `currentTeamId` (the selection seed) gets reset to the newly created team after a
 * detour - it's what SelectTeamDialogComponent's Save compares against to decide whether the
 * selection genuinely differs from what's persisted, so a freshly-created-and-auto-selected team
 * still triggers `onSave` instead of being mistaken for "unchanged".
 */
@Injectable({
  providedIn: 'root',
})
export class SelectTeamDialogService {
  private readonly dialog = inject(MatDialog);

  public open(
    teams: ITeam[],
    currentTeamId: string | null,
    onSave: (teamId: string | null) => Observable<unknown>,
  ): Observable<ISelectTeamDialogResult> {
    const result$ = new Subject<ISelectTeamDialogResult>();

    this.openSelectTeam(teams, currentTeamId, currentTeamId, onSave, result$);

    return result$.asObservable();
  }

  private openSelectTeam(
    teams: ITeam[],
    currentTeamId: string | null,
    savedTeamId: string | null,
    onSave: (teamId: string | null) => Observable<unknown>,
    result$: Subject<ISelectTeamDialogResult>,
  ): void {
    const dialogRef = this.dialog.open<SelectTeamDialogComponent, ISelectTeamDialogData, TSelectTeamDialogCloseResult>(
      SelectTeamDialogComponent,
      {
        data: { currentTeamId, onSave, savedTeamId, teams } as ISelectTeamDialogData,
        disableClose: true,
        minWidth: '700px',
      },
    );

    dialogRef.afterClosed().subscribe((closeResult) => {
      if (closeResult?.type === 'addTeam') {
        this.openAddTeam(teams, closeResult.selectedTeamId, savedTeamId, onSave, result$);

        return;
      }

      result$.next({ teamId: closeResult?.type === 'save' ? closeResult.teamId : undefined, teams });
      result$.complete();
    });
  }

  private openAddTeam(
    teams: ITeam[],
    fallbackTeamId: string | null,
    savedTeamId: string | null,
    onSave: (teamId: string | null) => Observable<unknown>,
    result$: Subject<ISelectTeamDialogResult>,
  ): void {
    const dialogRef = this.dialog.open<AddTeamDialogComponent, undefined, ITeam | undefined>(AddTeamDialogComponent, {
      disableClose: true,
      minWidth: '700px',
    });

    dialogRef.afterClosed().subscribe((newTeam) => {
      const updatedTeams = newTeam ? [...teams, newTeam] : teams;
      const nextCurrentTeamId = newTeam ? newTeam.id : fallbackTeamId;

      this.openSelectTeam(updatedTeams, nextCurrentTeamId, savedTeamId, onSave, result$);
    });
  }
}
